import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Message as MessageBox } from 'components/shared';
import LoadingSpinner from 'components/LoadingSpinner';

import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import OutlinedInput from '@material-ui/core/OutlinedInput';

import { useForm } from 'react-hook-form';
import useApi, {
  GET_AVAILABLE_USERS_TO_SHARE_DASHBOARDS_WITH,
  SHARE_DASHBOARDS_TO_USER
} from 'hooks/useApi';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%'
  },
  closeBtn: {
    marginLeft: '10px'
  },
  form: {
    margin: '50px auto',
    width: '60%'
  },
  input: {
    marginBottom: '10px',
    '&:hover': {
      color: theme.palette.primary.main
    },
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        color: theme.palette.primary.main,
        borderColor: theme.palette.accent.main
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main
      }
    }
  },
  buttonContainer: {
    '& button ~ button': {
      marginTop: '10px'
    }
  }
}));

const DashboardsShareForm = ({ dashboardId, onClose }) => {
  const classes = useStyles();

  const { register, handleSubmit, formState, errors } = useForm({
    mode: 'onChange',
    defaultValues: {
      userId: null
    }
  });

  const [state, makeRequest] = useApi();
  const [sendState, sendRequest] = useApi();

  const { isLoading, isError, data, error } = state;

  const {
    error: responseError,
    isError: isErrorResponse,
    data: responseData,
    isLoading: isResponseReady
  } = sendState;

  useEffect(() => {
    makeRequest(GET_AVAILABLE_USERS_TO_SHARE_DASHBOARDS_WITH());
  }, []); // eslint-disable-line

  const onSubmit = formData => {
    const dataToSubmit = {
      dashboardId,
      userIds: [Number(formData.userId)]
    };

    const params = SHARE_DASHBOARDS_TO_USER({ ...dataToSubmit });
    sendRequest(params);
  };

  return (
    <div className={classes.root}>
      <Box color="white" bgcolor="#2e6292" p={2}>
        <Typography variant="h5" className={classes.headerColor}>
          Share dashboard to another user
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <Grid container direction="row" justify="flex-start" alignItems="stretch">
          {data && !isLoading && (
            <Grid item md={12} xs={12}>
              <FormControl
                classes={{
                  root: classes.input
                }}
                variant="outlined"
                className={classes.formControl}
                fullWidth
              >
                <InputLabel htmlFor="user_id">Users</InputLabel>
                <Select
                  native
                  defaultValue={data.name}
                  inputRef={register({ required: 'Please select a valid user.' })}
                  input={
                    <OutlinedInput
                      classes={{
                        root: classes.input
                      }}
                      name="userId"
                      id="user_id"
                      labelWidth={90}
                    />
                  }
                >
                  {data.map(user => (
                    <option key={`${user.id} - ${user.name}`} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {isLoading && <LoadingSpinner />}

          {(isError || isErrorResponse) && (
            <MessageBox type="error" text={error || responseError} isError />
          )}
          {!isErrorResponse && responseData && (
            <MessageBox type="success" text={responseData} isError />
          )}
          {errors.userId && <MessageBox type="error" text={errors.userId.message} isError />}
          <Grid item md={12} xs={12}>
            <Box
              px={2}
              textAlign="center"
              marginTop="30px"
              className={!responseData && !errors.userId ? classes.buttonContainer : null}
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth={false}
                disabled={isError || isLoading || isResponseReady || !formState.isValid}
              >
                Share Dashboard
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth={false}
                onClick={onClose}
                className={classes.closeBtn}
              >
                Close
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

DashboardsShareForm.propTypes = {
  dashboardId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default DashboardsShareForm;
